'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { useWhatsApp } from '@/components/whatsapp-provider';

// Errors we treat as transient: an aborted fetch (iOS Safari throws
// "Load failed", Chrome throws "Failed to fetch" / "network error") or a
// chunk that failed to download mid-navigation. These are not user-facing
// bugs -- the user simply needs to retry. We still log them so admin sees
// them, but auto-reset the boundary once so the user never sees the
// "Something went wrong" page for a hiccup.
const TRANSIENT_MESSAGE = /^(Load failed|Failed to fetch|NetworkError|network error|The Internet connection appears to be offline)/i;
const TRANSIENT_NAME = /^(ChunkLoadError|AbortError)$/;
const AUTO_RESET_KEY = 'ffl_err_autoreset_ts';
const AUTO_RESET_WINDOW_MS = 5000;

function isTransient(error: Error): boolean {
  if (TRANSIENT_NAME.test(error.name)) return true;
  if (TRANSIENT_MESSAGE.test(error.message || '')) return true;
  return false;
}

// Returns true if we should auto-reset (and reserves the slot in sessionStorage
// so a repeat error within the window falls through to the UI).
function tryClaimAutoReset(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const last = Number(sessionStorage.getItem(AUTO_RESET_KEY) || 0);
    if (Date.now() - last < AUTO_RESET_WINDOW_MS) return false;
    sessionStorage.setItem(AUTO_RESET_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { href } = useWhatsApp();
  const waText = error.digest
    ? `Hi, I hit an error on Frags For Less. Reference: ${error.digest}`
    : 'Hi, I hit an error on Frags For Less.';
  // Computed once at mount. Transient errors that claim the auto-reset slot
  // suppress the UI and schedule a reset; everything else falls through.
  const [autoResetting] = useState(() => isTransient(error) && tryClaimAutoReset());

  useEffect(() => {
    const transient = isTransient(error);
    void import('@/lib/errors/log-client').then(({ logClientError }) => {
      logClientError({
        source: 'client',
        severity: transient ? 'warn' : 'fatal',
        message: error.message || 'Unknown rendering error',
        stack: error.stack ?? null,
        digest: error.digest ?? null,
        tags: transient
          ? ['react_error_boundary', 'transient_network', autoResetting ? 'auto_reset' : 'auto_reset_skipped']
          : ['react_error_boundary'],
      });
    });
    if (autoResetting) {
      const t = setTimeout(reset, 400);
      return () => clearTimeout(t);
    }
  }, [error, reset, autoResetting]);

  if (autoResetting) {
    return null;
  }

  return (
    <main id="main" className="flex min-h-[80vh] items-center justify-center bg-[var(--background)] px-4 py-16">
      <div className="w-full max-w-md text-center">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[var(--text-secondary)] sm:text-4xl">
          Something went wrong
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
          Please try again. If the problem continues, message us on WhatsApp.
        </p>
        {error.digest ? (
          <p className="mt-2 font-mono text-[11px] text-[var(--text-secondary)]/60">
            Ref: {error.digest}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col gap-3">
          <button
            onClick={reset}
            style={{
              backgroundImage:
                'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
            }}
            className="font-display inline-flex w-full items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:brightness-105"
          >
            Try again
          </button>
          <a
            href={href(waText)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-display inline-flex w-full items-center justify-center rounded-full border border-[var(--border)] px-5 py-2.5 text-sm font-semibold text-[var(--text-secondary)] transition-colors duration-200 hover:border-[var(--accent)] hover:text-[var(--accent)]"
          >
            Message us on WhatsApp
          </a>
          <Link
            href="/"
            className="text-xs text-[var(--text-secondary)] hover:text-[var(--accent)]"
          >
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
}
